import { useMemo } from "react";
import {
  FACEBOOK as FACEBOOK_CONSTANT,
  INSTAGRAM as INSTAGRAM_CONSTANT,
  META as META_CONSTANT,
  TIKTOK as TIKTOK_CONSTANT,
  YOUTUBE as YOUTUBE_CONSTANT,
} from "../../../../utils/constants";
import { createPlatformDefaultValues } from "../utils/platforms";

const PLATFORM_KEY_MAP = {
  [META_CONSTANT]: INSTAGRAM_CONSTANT,
  [FACEBOOK_CONSTANT]: FACEBOOK_CONSTANT,
  [INSTAGRAM_CONSTANT]: INSTAGRAM_CONSTANT,
  [TIKTOK_CONSTANT]: TIKTOK_CONSTANT,
  [YOUTUBE_CONSTANT]: YOUTUBE_CONSTANT,
};

const normalizePlatformKey = (platformKey = "") => {
  if (typeof platformKey !== "string") return null;
  const normalized = platformKey.trim().toLowerCase();
  return PLATFORM_KEY_MAP[normalized] || null;
};

const normalizePlatformsDataMap = (platformsData = {}) =>
  Object.entries(platformsData || {}).reduce((acc, [key, value]) => {
    const normalizedKey = normalizePlatformKey(key);
    if (normalizedKey) {
      acc[normalizedKey] = value || {};
    }
    return acc;
  }, {});

const createInstagramAccountOption = (account) => {
  if (!account) return null;
  return {
    id: account.integrationID || account.id || account.instagramID || "",
    name: account.name || account.instagramID || account.integrationID || "",
    integrationID: account.integrationID || "",
    instagramID: account.instagramID || "",
    pageID: account.pageID || "",
  };
};

const findInstagramAccountOption = (platformData, businessAccounts = []) => {
  if (!platformData) return null;

  const matchedAccount =
    businessAccounts.find(
      (account) =>
        platformData.instagramID &&
        account.instagramID === platformData.instagramID
    ) ||
    businessAccounts.find(
      (account) =>
        platformData.integrationID &&
        account.integrationID === platformData.integrationID
    );

  if (matchedAccount) {
    return createInstagramAccountOption(matchedAccount);
  }

  if (platformData.instagramID || platformData.integrationID) {
    return {
      id: platformData.integrationID || platformData.instagramID,
      name:
        platformData.instagramID ||
        platformData.integrationID ||
        "Instagram Account",
      integrationID: platformData.integrationID || "",
      instagramID: platformData.instagramID || "",
      pageID: platformData.pageID || "",
    };
  }

  return null;
};

const findFacebookAccountOption = (platformData, facebookPages = []) => {
  if (!platformData) return null;

  const matchedPage = facebookPages.find(
    (page) => platformData.pageID && page.id === platformData.pageID
  );

  if (matchedPage) {
    return {
      id: matchedPage.id,
      name: matchedPage.name || matchedPage.id,
      integrationID: matchedPage.integrationID || "",
      imageURL: matchedPage.imageURL || "",
    };
  }

  if (platformData.pageID || platformData.integrationID) {
    return {
      id: platformData.pageID || platformData.integrationID,
      name: platformData.pageName || platformData.pageID || "Facebook Page",
      integrationID: platformData.integrationID || "",
      imageURL: platformData.imageURL || "",
    };
  }

  return null;
};

const findTikTokAccountOption = (platformData, integrations = {}) => {
  if (!platformData?.integrationID) return null;
  const integrationID = platformData.integrationID;
  const tikTokIntegration = integrations?.tiktok?.[integrationID];

  return {
    id: integrationID,
    name: tikTokIntegration?.name || platformData.accountName || integrationID,
    integrationID,
  };
};

const findYouTubeAccountOption = (platformData, integrations = {}) => {
  const integrationID = platformData?.integrationID || platformData?.channelID;
  if (!integrationID) return null;

  const youtubeIntegration = integrations?.youtube?.[integrationID];
  const channelID =
    youtubeIntegration?.channelID || platformData?.channelID || "";

  return {
    id: integrationID,
    name:
      youtubeIntegration?.channelTitle ||
      youtubeIntegration?.channelID ||
      platformData?.channelTitle ||
      integrationID,
    integrationID,
    channelID,
  };
};

export const useShareModalEdit = ({
  isEditing,
  editingData,
  defaultLanguage,
  scheduledDate,
  uniqueInstagramBusinessList,
  uniqueFacebookPagesList,
  integrations,
}) => {
  const editingOriginalData = editingData?.originalData || null;

  const editingHydratedState = useMemo(() => {
    if (!isEditing || !editingOriginalData) return null;

    const normalizedPlatforms = Array.from(
      new Set(
        (editingOriginalData.platforms || [])
          .map((platform) => normalizePlatformKey(platform))
          .filter(Boolean)
      )
    );

    const normalizedPlatformsData = normalizePlatformsDataMap(
      editingOriginalData.platformsData || {}
    );

    const platformsData = createPlatformDefaultValues();

    normalizedPlatforms.forEach((platformKey) => {
      const platformData = normalizedPlatformsData[platformKey] || {};

      if (platformKey === INSTAGRAM_CONSTANT) {
        platformsData[INSTAGRAM_CONSTANT] = {
          ...platformsData[INSTAGRAM_CONSTANT],
          account: findInstagramAccountOption(
            platformData,
            uniqueInstagramBusinessList
          ),
          contentType: platformData.mediaType,
          collaborators: Array.isArray(platformData.collaborators)
            ? platformData.collaborators
            : [],
        };
      }

      if (platformKey === FACEBOOK_CONSTANT) {
        platformsData[FACEBOOK_CONSTANT] = {
          ...platformsData[FACEBOOK_CONSTANT],
          account: findFacebookAccountOption(
            platformData,
            uniqueFacebookPagesList
          ),
          contentType: platformData.mediaType,
        };
      }

      if (platformKey === TIKTOK_CONSTANT) {
        platformsData[TIKTOK_CONSTANT] = {
          ...platformsData[TIKTOK_CONSTANT],
          account: findTikTokAccountOption(platformData, integrations),
          privacy_level: platformData.privacy || "",
          disable_duet: Boolean(platformData.allow?.duet),
          disable_stitch: Boolean(platformData.allow?.stitch),
          disable_comment: Boolean(platformData.allow?.comments),
          is_aigc: Boolean(platformData.allow?.is_aigc),
          auto_add_music: Boolean(platformData.allow?.auto_add_music),
        };
      }

      if (platformKey === YOUTUBE_CONSTANT) {
        platformsData[YOUTUBE_CONSTANT] = {
          ...platformsData[YOUTUBE_CONSTANT],
          account: findYouTubeAccountOption(platformData, integrations),
          title: platformData.title || "",
          privacyStatus: platformData.privacyStatus || "",
          notifySubscribers: Boolean(platformData.notifySubscribers),
          madeForKids: Boolean(platformData.madeForKids),
        };
      }
    });

    const resolveScheduledDate = () => {
      if (scheduledDate instanceof Date) {
        return Number.isNaN(scheduledDate.getTime()) ? null : scheduledDate;
      }

      if (editingOriginalData.scheduledDate) {
        const parsed = new Date(editingOriginalData.scheduledDate);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      return null;
    };

    return {
      formValues: {
        language: editingOriginalData.language || defaultLanguage,
        description: editingOriginalData.description || "",
        scheduledDate: resolveScheduledDate(),
        platformsData,
        platforms: normalizedPlatforms,
      },
      selectedPlatforms: normalizedPlatforms,
    };
  }, [
    isEditing,
    editingOriginalData,
    defaultLanguage,
    scheduledDate,
    uniqueInstagramBusinessList,
    uniqueFacebookPagesList,
    integrations,
  ]);

  return {
    editingOriginalData,
    editingHydratedState,
  };
};

export default useShareModalEdit;
